import styled from '@emotion/styled';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { color, font, mediaQueries, shadow } from 'src/shared/styles/variables';
import { ProgressBar } from '../components/ProgressBar';

import { AbandonButton } from '../components/AbandonButton';
import { CommonStepButtons } from '../components/CommonStepButtons';
import { QuestionWrapper } from '../components/QuestionWrapper';
import { ScreenTitle } from '../components/ScreenTitle';
import { ScreenWrapper } from '../components/ScreenWrapper';
import { AnswerOptionData, IntakeFormProps } from '../models/intakeForm';
import { getAnswerOptionData, getQuestionDataFor } from '../selectors/intakeFormSelectors';

const Answers = styled('ul')({
    listStyleType: 'none',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginBottom: 0,
    marginTop: 30,
    [mediaQueries.md]: {
        flexDirection: 'column',
    },
});

interface AnswerProps {
    isHighlighted?: boolean;
    needsMarginLeft?: boolean;
    onClick: () => void;
}

const Answer = styled('li')<AnswerProps>(({ isHighlighted }) => ({
    backgroundColor: color.white,
    color: color.regentGray,
    padding: '1rem',
    border: `2px solid ${isHighlighted ? color.royalBlue : color.linkWaterLight}`,
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    userSelect: 'none',
    borderRadius: 5,
    marginBottom: 10,
    width: '100%',
    textAlign: 'center',
    '&:hover': {
        boxShadow: shadow.floatingShadow,
    },
    '&:focus': {
        boxShadow: shadow.floatingShadow,
    },
    ...font.medium(font.size.base),
}));

export const IntakeMultiSelectScreen: React.FC<IntakeFormProps> = props => {
    const {
        onAbandonClick,
        skipQuestion,
        saveResponse,
        goBack,
        currentScreen,
        responses,
        screenCount,
    } = props;
    const { answerIds = [], questionId = '', questionText, screenIndex } = getQuestionDataFor(
        currentScreen,
    );
    const answers = answerIds.map(id => getAnswerOptionData(id));
    const responsesAsArray = Array.isArray(responses) ? responses : [];

    const [chosenResponses, setChosenResponses] = useState(
        responsesAsArray.reduce((accum, curr) => ({ ...accum, [curr]: !!curr }), {}),
    );
    const { t } = useTranslation();

    const toggleChosen = (id: string) => {
        let nextResponses = { ...chosenResponses };

        const alreadySelected = chosenResponses[id];
        if (alreadySelected) {
            nextResponses[id] = !chosenResponses[id];
        } else {
            // the first and second answers can be selected together but if user decide to select the third answer
            // we have to unselect the first two since the answers are contradictory
            if (id === answers[2].value) {
                nextResponses = { [answers[2].value]: true };
            } else {
                nextResponses = { ...nextResponses, [answers[2].value]: false, [id]: true };
            }
        }

        setChosenResponses(nextResponses);
    };

    const submitResponse = () => {
        saveResponse(
            questionId,
            Object.keys(chosenResponses).filter(key => !!chosenResponses[key]),
        );
    };

    const handleAbandonClick = () => {
        onAbandonClick('');
    };

    return (
        <ScreenWrapper>
            <ProgressBar percentage={Math.floor((screenIndex / screenCount) * 100)} />
            <AbandonButton onClick={handleAbandonClick} />
            <QuestionWrapper>
                <ScreenTitle>{t(questionText)}</ScreenTitle>
                <Answers>
                    {answers.map((answerOption: AnswerOptionData) => (
                        // eslint-disable-next-line react/jsx-key
                        <Answer
                            onClick={() => toggleChosen(answerOption.value)}
                            isHighlighted={!!chosenResponses[answerOption.value]}
                        >
                            {t(answerOption.text)}
                        </Answer>
                    ))}
                </Answers>
                <CommonStepButtons
                    disableForward={Object.values(chosenResponses).filter(x => x).length === 0}
                    onSkipClick={skipQuestion}
                    onForwardClick={submitResponse}
                    onBackClick={goBack}
                />
            </QuestionWrapper>
        </ScreenWrapper>
    );
};
